// Based on Bootstrap's color mode toggler reference code

export type ThemeName = "light" | "dark" | "auto";

export interface ThemeInfo
{
    name: ThemeName;
    label: string;
    icon: string;
}

//NOTE: This should match the values in _ThemeSwitcher.cshtml

export const LIGHT_MODE: ThemeInfo =
{
    name: "light",
    label: "Light",
    icon: "light-mode",
};

export const DARK_MODE: ThemeInfo =
{
    name: "dark",
    label: "Dark",
    icon: "dark-mode",
};

export const AUTO_MODE: ThemeInfo =
{
    name: "auto",
    label: "Auto",
    icon: "routine",
};

export const THEMES: ThemeInfo[] = [LIGHT_MODE, DARK_MODE, AUTO_MODE];

const THEME_ICONS: string[] = THEMES.map(theme => `material-symbols-${theme.icon}`);

const storedTheme: ThemeName | null = localStorage.getItem("theme") as ThemeName;

/**
 * Gets the user's preferred theme
 * @returns The theme
 */
export function getPreferredTheme(): ThemeName
{
    if (storedTheme != null)
        return storedTheme;

    return (window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light");
};

/**
 * Applies the specified theme to the document
 * @param theme The theme to apply
 */
export function setTheme(theme: ThemeName)
{
    if (theme === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches)
    {
        document.documentElement.setAttribute("data-bs-theme", "dark");
    }
    else
    {
        document.documentElement.setAttribute("data-bs-theme", theme);
    }
};

function getThemeLinkElements(): NodeListOf<Element>
{
    return document.querySelectorAll("[data-bs-theme-value]");
}

function showActiveTheme(theme: ThemeName, focus: boolean = false)
{
    const themeInstance: ThemeInfo | null = THEMES.find(someTheme => someTheme.name == theme) ?? null;

    const themeSwitcher: HTMLElement | null = document.querySelector("#bd-theme");

    if (themeSwitcher == null)
        return;

    //const themeSwitcherText = document.querySelector("#bd-theme-text");
    const themeSwitcherIcon: Element | null = document.querySelector("#bd-theme-icon");
    const btnToActive: HTMLElement | null = document.querySelector(`[data-bs-theme-value="${theme}"]`);

    getThemeLinkElements()
        .forEach(element =>
        {
            element.classList.remove("active");
            element.setAttribute("aria-pressed", "false");
        });

    btnToActive?.classList?.add("active");
    btnToActive?.setAttribute("aria-pressed", "true");
    themeSwitcherIcon?.classList?.remove(...THEME_ICONS);

    if (themeInstance != null)
    {
        themeSwitcherIcon?.classList?.add(`material-symbols-${themeInstance.icon}`);
    }

    const themeSwitcherLabel: string = `$Theme: (${btnToActive?.dataset?.bsThemeValue})`;

    themeSwitcher.setAttribute("aria-label", themeSwitcherLabel);

    if (focus)
    {
        themeSwitcher.focus();
    }
};

/**
 * Initializes the theme switcher code, setting the theme and hooking up event handlers
 */
export function init()
{
    setTheme(getPreferredTheme());

    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () =>
    {
        setTheme(getPreferredTheme());
    });

    window.addEventListener("DOMContentLoaded", () =>
    {
        showActiveTheme(getPreferredTheme());

        getThemeLinkElements()
            .forEach(element =>
            {
                element.addEventListener("click", () =>
                {
                    const theme: ThemeName | null = element.getAttribute("data-bs-theme-value") as ThemeName;

                    localStorage.setItem("theme", theme);

                    setTheme(theme);
                    showActiveTheme(theme, true);
                });
            });
    });
}
